import { APP_PAGES } from "@dune/types";
import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarGroup,
	SidebarGroupContent,
	SidebarHeader,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarRail,
	SidebarTrigger,
	useSidebar,
} from "@dune/ui";
import { cn } from "@dune/ui";
import {
	AccountingDocument03,
	BusinessDealHandshake,
	ChatBubbleIcon,
	DatabaseCoinIcon,
	DocumentCheckIcon,
	DocumentIcon,
	Home01,
	InvoiceIcon,
	ScalesIcon,
	ShieldIcon,
	TaskListApproveIcon,
	ToolWrenchIcon,
	UserIcon,
} from "@dune/ui";
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { type NavigationItem, isActive } from "../../utils/navigation";
import { Role, RoleEnum } from "../../../../../packages/vars/src";

interface NavigationGroup {
	items: NavigationItem[];
}

const navigationConfig: NavigationGroup[] = [
	{
		items: [
			{
				label: "Dashboard",
				icon: Home01,
				href: APP_PAGES.ROOT,
				isActive: true,
				roles: [
					RoleEnum.DUNE,
					RoleEnum.BROKER,
					RoleEnum.SYNDIC,
					RoleEnum.INSURER,
				],
			},
			{
				label: "Apporteurs",
				icon: BusinessDealHandshake,
				href: APP_PAGES.BROKER_LIST,
				roles: [RoleEnum.DUNE],
				activePattern: ["/brokers", "/brokers/:id"],
			},
			{
				label: "Projets",
				icon: AccountingDocument03,
				href: APP_PAGES.PROJECTS.index,
				activePattern: ["/projects", "/projects/:id"],
				roles: [
					RoleEnum.DUNE,
					RoleEnum.BROKER,
					RoleEnum.SYNDIC,
					RoleEnum.INSURER,
				],
			},
			{
				label: "Propositions",
				icon: InvoiceIcon,
				href: APP_PAGES.PROPOSALS.index,
				activePattern: ["/proposals", "/proposals/:id"],
				roles: [
					RoleEnum.DUNE,
					RoleEnum.BROKER,
					RoleEnum.SYNDIC,
					RoleEnum.INSURER,
				],
			},
			{
				label: "Contrats",
				icon: DocumentCheckIcon,
				href: APP_PAGES.CONTRACTS.index,
				activePattern: ["/contracts", "/contracts/:id"],
				roles: [
					RoleEnum.DUNE,
					RoleEnum.BROKER,
					RoleEnum.SYNDIC,
					RoleEnum.INSURER,
				],
			},
			{
				label: "Quittances",
				icon: DatabaseCoinIcon,
				href: APP_PAGES.RECEIPTS.index,
				roles: [RoleEnum.DUNE, RoleEnum.BROKER, RoleEnum.INSURER],
			},
			{
				label: "Sinistres",
				icon: ShieldIcon,
				href: APP_PAGES.CLAIMS,
				disabled: true,
				roles: [RoleEnum.DUNE],
			},
		],
	},
	{
		items: [
			{
				label: "Contrats cadres",
				icon: DocumentIcon,
				href: APP_PAGES.FRAMEWORK_CONTRACTS,
				disabled: true,
				roles: [RoleEnum.DUNE],
			},
			{
				label: "Clients",
				icon: UserIcon,
				href: APP_PAGES.CLIENTS.index,
				roles: [RoleEnum.DUNE, RoleEnum.BROKER],
			},
			{
				label: "Intervenants",
				icon: ToolWrenchIcon,
				href: APP_PAGES.CONTRACTORS.index,
				roles: [RoleEnum.DUNE],
			},
			{
				label: "Assureurs",
				icon: ShieldIcon,
				href: APP_PAGES.INSURERS_MANAGEMENT,
				roles: [RoleEnum.DUNE],
			},
		],
	},
	{
		items: [
			{
				label: "Comptabilité",
				icon: ScalesIcon,
				href: APP_PAGES.ACCOUNTING,
				disabled: true,
				roles: [RoleEnum.DUNE, RoleEnum.BROKER, RoleEnum.INSURER],
			},
			{
				label: "Tâches",
				icon: TaskListApproveIcon,
				href: APP_PAGES.TASKS,
				roles: [RoleEnum.DUNE, RoleEnum.BROKER, RoleEnum.SYNDIC],
			},
			{
				label: "Communications",
				icon: ChatBubbleIcon,
				href: APP_PAGES.COMMUNICATIONS,
				roles: [RoleEnum.DUNE],
			},
			{
				label: "Binders",
				icon: AccountingDocument03,
				href: APP_PAGES.BINDERS.index,
				activePattern: ["/binders", "/binders/:id"],
				roles: [RoleEnum.DUNE],
			},
		],
	},
];

const SidebarIcon = () => {
	const { state } = useSidebar();
	const isCollapsed = state === "collapsed";

	return (
		<div className="flex items-center h-12 px-2 overflow-hidden">
			<img
				src="/dune_logo.png"
				alt="DUNE Logo"
				className={cn(
					"h-6 transition-all duration-500 ease-out",
					isCollapsed
						? "opacity-0 w-0 scale-0 -translate-x-4"
						: "opacity-100 w-auto scale-100 translate-x-0",
				)}
			/>
		</div>
	);
};

interface AppSidebarProps {
	className?: string;
	userRole?: Role;
}

export const AppSidebar = React.forwardRef<HTMLDivElement, AppSidebarProps>(
	({ className, userRole }, ref) => {
		const location = useLocation();

		const filteredNavigationConfig = navigationConfig
			.map((group) => ({
				...group,
				items: group.items.filter((item) => {
					if (!item.roles) return true;
					return userRole && item.roles.includes(userRole);
				}),
			}))
			.filter((group) => group.items.length > 0);

		return (
			<Sidebar ref={ref} collapsible="icon" className={cn(className)}>
				<SidebarHeader className="flex items-center justify-start h-16 mb-4">
					<SidebarTrigger />
					<SidebarIcon />
				</SidebarHeader>
				<SidebarContent>
					<SidebarMenu>
						{filteredNavigationConfig.map((group, groupIndex) => (
							<React.Fragment
								key={`group-${group.items[0]?.label || groupIndex}`}
							>
								{groupIndex > 0 && <div className="h-8" />}
								<SidebarGroup>
									<SidebarGroupContent>
										{group.items.map((item) => (
											<SidebarMenuItem key={item.label}>
												{item.disabled ? (
													<div className="w-full opacity-50 cursor-not-allowed">
														<SidebarMenuButton
															asChild
															isActive={false}
															tooltip={item.label}
														>
															<div>
																<item.icon className="text-gray-400" />
																<span className="text-gray-500">
																	{item.label}
																</span>
															</div>
														</SidebarMenuButton>
													</div>
												) : (
													<Link to={item.href} className="w-full">
														<SidebarMenuButton
															asChild
															isActive={isActive(location.pathname, item)}
															tooltip={item.label}
														>
															<div>
																<item.icon
																	className={cn("text-gray-700", {
																		"stroke-[1.75]": isActive(
																			location.pathname,
																			item,
																		),
																	})}
																/>
																<span
																	className={cn("text-gray-900", {
																		"font-semibold": isActive(
																			location.pathname,
																			item,
																		),
																	})}
																>
																	{item.label}
																</span>
															</div>
														</SidebarMenuButton>
													</Link>
												)}
											</SidebarMenuItem>
										))}
									</SidebarGroupContent>
								</SidebarGroup>
							</React.Fragment>
						))}
					</SidebarMenu>
				</SidebarContent>
				<SidebarFooter />
				<SidebarRail />
			</Sidebar>
		);
	},
);

AppSidebar.displayName = "AppSidebar";

export default AppSidebar;
